/**
 * Ability rating ↔ level mapping, warm start and the rating persisted after a
 * run. Spec: docs/GAME_DESIGN.md § 3; tuning notes: docs/ADAPTIVE.md.
 */
import { ADAPTIVE, LEVEL_COUNT, LEVEL_WIDTH, RATING_MAX } from '../config.ts';
import { clamp } from '../math/stats.ts';
import type { AdaptiveConstants } from './types.ts';

/**
 * Tolerance for `floor(R / W)`. LEVEL_WIDTH is not exactly representable, so a
 * rating computed as `(L - 1) * LEVEL_WIDTH` can divide back to `L - 1 - 1e-16`.
 * Nudging by a hair keeps exact band boundaries on the upper level.
 */
const BAND_EPS = 1e-9;

/** Clamp a rating into [0, RATING_MAX]; non-finite input becomes 0. */
export function clampRating(rating: number): number {
  return Number.isFinite(rating) ? clamp(rating, 0, RATING_MAX) : 0;
}

/** Level whose band contains `rating`, without hysteresis: floor(R / W) + 1, clamped 1–24. */
function bandLevel(rating: number): number {
  return clamp(Math.floor(rating / LEVEL_WIDTH + BAND_EPS) + 1, 1, LEVEL_COUNT);
}

/**
 * Level for a rating. With `currentLevel` given, applies hysteresis around the
 * band boundaries: move up only once R is HYSTERESIS points past the upper
 * boundary, move down only once it is HYSTERESIS points below the lower one.
 */
export function levelForRating(rating: number, currentLevel?: number, k: AdaptiveConstants = ADAPTIVE): number {
  const r = clampRating(rating);
  if (currentLevel === undefined) return bandLevel(r);
  const current = clamp(Math.round(currentLevel), 1, LEVEL_COUNT);
  const up = bandLevel(r - k.HYSTERESIS);
  if (up > current) return up;
  const down = bandLevel(r + k.HYSTERESIS);
  if (down < current) return down;
  return current;
}

/**
 * Position of `rating` inside `level`'s band: 0 at the band's lower boundary,
 * 1 at its upper boundary. Clamped, so a rating held just outside the band by
 * hysteresis reads as 0 or 1.
 */
export function bandFraction(rating: number, level: number): number {
  return clamp((clampRating(rating) - (level - 1) * LEVEL_WIDTH) / LEVEL_WIDTH, 0, 1);
}

/**
 * Start rating for a new run from the persisted rating: WARM_START_LEVELS below
 * it so the player warms up and converges from below. null/undefined (first-ever
 * run) → 0.
 */
export function warmStartRating(savedRating: number | null | undefined, k: AdaptiveConstants = ADAPTIVE): number {
  if (savedRating === null || savedRating === undefined) return 0;
  return clampRating(clampRating(savedRating) - k.WARM_START_LEVELS * LEVEL_WIDTH);
}

/**
 * Rating to persist after a run: the final rating, but never more than
 * SAVE_PEAK_MARGIN_LEVELS below the run's peak — so a late-run collapse under
 * the pace ramp (or deliberate tanking) doesn't erase established progress.
 */
export function savedRatingAfterRun(finalRating: number, peakRating: number, k: AdaptiveConstants = ADAPTIVE): number {
  // peakRating is the DEMONSTRATED peak: never save above it (a promotion the
  // player died before proving must not poison the next warm start), and never
  // drop more than SAVE_PEAK_MARGIN_LEVELS below it (a late collapse under the
  // pace ramp shouldn't erase progress).
  const peak = clampRating(peakRating);
  const floor = peak - k.SAVE_PEAK_MARGIN_LEVELS * LEVEL_WIDTH;
  return clampRating(Math.min(peak, Math.max(clampRating(finalRating), floor)));
}
